import {
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Users,
  Calendar,
} from "lucide-react";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Separator } from "@/app/_module/components/ui/separator";

interface LeadDetailsCardProps {
  lead: Lead;
}

export const LeadDetailsCard = function ({ lead }: LeadDetailsCardProps) {
  // Format phone number from E.164 (+1XXXXXXXXXX) to (XXX) XXX-XXXX
  const formatPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return "";
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, "");
    // Handle 10-digit or 11-digit (with country code) numbers
    const nationalNumber = digits.length === 11 && digits.startsWith("1")
      ? digits.slice(1)
      : digits;
    if (nationalNumber.length === 10) {
      return `(${nationalNumber.slice(0, 3)}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`;
    }
    // Return original if not standard format
    return phone;
  };

  const getStatusColor = (status: string | null | undefined) => {
    if (!status) return "bg-gray-600 text-gray-100";
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case "new lead":
        return "bg-blue-100 text-blue-800";
      case "appt":
      case "appointment":
      case "scheduled":
        return "bg-amber-500 text-amber-100";
      case "underwriting":
      case "under writing":
        return "bg-cyan-600 text-cyan-100";
      case "sold & approved":
      case "approved":
        return "bg-emerald-600 text-emerald-100";
      case "bad number":
      case "bad #":
        return "bg-red-500 text-red-100";
      case "no sale":
        return "bg-red-600 text-red-100";
      case "do not call":
        return "bg-amber-800 text-amber-100";
      case "disposed":
        return "bg-gray-600 text-gray-100";
      default:
        return "bg-gray-600 text-gray-100";
    }
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Card className="border-2 border-green-200">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Lead Details
          </div>
          <Badge className={getStatusColor(lead.leadStat)}>{lead.leadStat || 'Unknown'}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Basic Info */}
        <div className="space-y-3">
          <div>
            <h3 className="text-xl font-semibold">{lead.name}</h3>
            <div className="flex items-center gap-1 mt-1">
              <Phone className="h-4 w-4 text-blue-600" />
              <span className="font-mono text-blue-600">{formatPhoneNumber(lead.phone)}</span>
            </div>
            {!!lead.email && (
              <div className="flex items-center gap-2 mt-1">
                <Mail className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">{lead.email}</span>
              </div>
            )}
          </div>

          {/* Personal Info */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {!!lead.age && (
              <div>
                <span className="text-gray-500">Age:</span> {lead.age}
              </div>
            )}
            {!!lead.gender && (
              <div>
                <span className="text-gray-500">Gender:</span> {lead.gender}
              </div>
            )}
            {!!lead.dob && (
              <div className="col-span-2">
                <Calendar className="h-3 w-3 inline mr-1 text-gray-500" />
                <span className="text-gray-500">Birthday:</span> {lead.dob}
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Address */}
        {!!(lead.address || lead.city || lead.state) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-600" />
              <span className="font-medium text-sm">Address</span>
            </div>
            <div className="text-sm text-gray-700 pl-6">
              {!!lead.address && <div>{lead.address}</div>}
              {!!(lead.city || lead.state || lead.zipCode) && (
                <div>
                  {[lead.city, lead.state, lead.zipCode]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Employment */}
        {!!lead.employer && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-gray-600" />
              <span className="font-medium text-sm">Employment</span>
            </div>
            <div className="text-sm text-gray-700 pl-6">
              {lead.employer}
              {!!lead.earnedIncome && (
                <div className="text-xs text-gray-500">
                  Income: {lead.earnedIncome}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Family */}
        {!!(lead.maritalStatus || lead.childrenDependents) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-600" />
              <span className="font-medium text-sm">Family</span>
            </div>
            <div className="text-sm text-gray-700 pl-6">
              {!!lead.maritalStatus && <div>Status: {lead.maritalStatus}</div>}
              {!!lead.childrenDependents && (
                <div>Children: {lead.childrenDependents}</div>
              )}
            </div>
          </div>
        )}

        {/* Call History */}
        {!!(lead.lastCall || lead.longestCall) && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-600" />
              <span className="font-medium text-sm">Call History</span>
            </div>
            <div className="text-sm text-gray-700 pl-6">
              {!!lead.lastCall && <div>Last Call: {formatDateTime(lead.lastCall)}</div>}
              {!!lead.longestCall && (
                <div>Longest Call: {lead.longestCall}</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
